import { queryOptions, skipToken, UseQueryResult, useQuery } from '@tanstack/react-query'
import { Config, useConfig } from 'wagmi'
import { getBlockNumber } from 'wagmi/actions'

export interface UseEnsureBlockSyncParams {
  targetBlock: bigint | undefined
  enabled?: boolean
}

export interface UseEnsureBlockSyncResult {
  synced: boolean
  currentBlock: bigint
  targetBlock: bigint
}

export function useEnsureBlockSync({
  targetBlock,
  enabled,
}: UseEnsureBlockSyncParams): UseQueryResult<UseEnsureBlockSyncResult, Error> {
  const config = useConfig()

  return useQuery(ensureBlockSyncQueryOptions(config, { targetBlock, enabled }))
}

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export function ensureBlockSyncQueryOptions(
  config: Config,
  { targetBlock, enabled }: { targetBlock: bigint | undefined; enabled: boolean | undefined },
) {
  return queryOptions({
    queryKey: ['ensure-block-sync', targetBlock?.toString()],
    queryFn:
      enabled && targetBlock !== undefined
        ? async () => {
            const currentBlock = await getBlockNumber(config)

            if (currentBlock >= targetBlock) {
              return { synced: true, currentBlock, targetBlock }
            }

            // Node hasn't caught up yet, throw to trigger retry
            throw new Error(`Node not synced yet. Current: ${currentBlock}, Target: ${targetBlock}`)
          }
        : skipToken,
    retryDelay: 200,
    retry: (failureCount) => failureCount <= 100, // 100 * 200ms = 20 seconds
  })
}
